import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Database from '../../../types/Database'
import { useTestDataStore } from '../../../stores/testDataStore'

type PayoutFilter = Database.PayoutStatus

const STATUS_OPTIONS: { value: PayoutFilter; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'processed', label: 'Processed' },
  { value: 'credited', label: 'Credited' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' }
]

const DEFAULT_FILTERS: PayoutFilter[] = ['pending']

interface PayoutRow {
  transaction: Database.Transaction
  hostName: string
  bankDetails: Database.BankDetails | null
  diveName: string
}

const statusColor = (
  status: Database.PayoutStatus
): 'default' | 'warning' | 'success' | 'info' | 'error' => {
  switch (status) {
    case 'pending':
      return 'warning'
    case 'processed':
      return 'success'
    case 'credited':
      return 'info'
    case 'cancelled':
      return 'error'
    default:
      return 'default'
  }
}

type DiveStatus = Database.Dive['status']

const DIVE_STATUS_COLORS: Record<
  DiveStatus,
  'success' | 'info' | 'warning' | 'error' | 'default'
> = {
  open: 'success',
  full: 'info',
  cancelled: 'warning',
  completed: 'default',
  removed: 'error'
}

interface DiveDetail {
  id: string
  siteName: string
  hostId: string
  description: string
  locationDisplay: string
  dateTimeDisplay: string
  status: DiveStatus
  price: number | null
  maxParticipants: number
  type: 'buddy' | 'guided'
  duration: number
  depthTier: string
  moderationReason: string | null
}

const formatAUD = (amount: number | null): string => {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(amount)
}

const PayoutManagementTab = () => {
  const showTestData = useTestDataStore(s => s.showTestData)
  const [payouts, setPayouts] = useState<PayoutRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeFilters, setActiveFilters] =
    useState<PayoutFilter[]>(DEFAULT_FILTERS)
  const [showCancelled, setShowCancelled] = useState(false)
  const [selectedPayout, setSelectedPayout] = useState<PayoutRow | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [processing, setProcessing] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  // Dive detail modal state
  const [diveDetailOpen, setDiveDetailOpen] = useState(false)
  const [diveDetailLoading, setDiveDetailLoading] = useState(false)
  const [selectedDiveDetail, setSelectedDiveDetail] =
    useState<DiveDetail | null>(null)
  const [selectedDiveHostName, setSelectedDiveHostName] = useState('')

  const handleDiveClick = async (payout: PayoutRow) => {
    setDiveDetailOpen(true)
    setDiveDetailLoading(true)
    setSelectedDiveHostName(payout.hostName)

    try {
      const db = getFirestore()
      const diveDoc = await getDoc(doc(db, 'dives', payout.transaction.diveId))
      if (diveDoc.exists()) {
        const data = diveDoc.data()
        const location = data.location
        const dateTime = data.dateTime
        setSelectedDiveDetail({
          id: payout.transaction.diveId,
          siteName: (data.siteName as string) || payout.transaction.diveId,
          hostId: data.hostId as string,
          description: data.description as string,
          locationDisplay: location
            ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(
                4
              )}`
            : '—',
          dateTimeDisplay: dateTime?.toDate?.()
            ? dateTime.toDate().toLocaleString()
            : '—',
          status: data.status as DiveStatus,
          price: (data.price as number | null) ?? null,
          maxParticipants: data.maxParticipants as number,
          type: data.type as 'buddy' | 'guided',
          duration: data.duration as number,
          depthTier: data.depthTier as string,
          moderationReason: (data.moderationReason as string | null) ?? null
        })
      } else {
        setSelectedDiveDetail(null)
      }
    } catch {
      setSelectedDiveDetail(null)
    } finally {
      setDiveDetailLoading(false)
    }
  }

  const currentStatuses = showCancelled
    ? [...activeFilters, 'cancelled' as PayoutFilter]
    : activeFilters.filter(f => f !== 'cancelled')

  const fetchPayouts = useCallback(
    async (statuses: PayoutFilter[]) => {
      setLoading(true)
      setError(null)
      try {
        const db = getFirestore()
        const transactionsRef = collection(db, 'transactions')

        // Firestore 'in' supports up to 30 values
        const q =
          statuses.length === 1
            ? query(
                transactionsRef,
                where('payoutStatus', '==', statuses[0]),
                where('testData', '==', showTestData),
                orderBy('createdAt', 'asc')
              )
            : query(
                transactionsRef,
                where('payoutStatus', 'in', statuses),
                where('testData', '==', showTestData),
                orderBy('createdAt', 'asc')
              )

        const snap = await getDocs(q)
        const transactions = snap.docs.map(
          d => ({ id: d.id, ...d.data() } as Database.Transaction)
        )

        // Resolve host data and dive names
        const hostIds = [...new Set(transactions.map(t => t.hostId))]
        const diveIds = [...new Set(transactions.map(t => t.diveId))]

        const hostMap = new Map<
          string,
          { displayName: string; bankDetails: Database.BankDetails | null }
        >()
        const diveMap = new Map<string, string>()

        await Promise.all(
          hostIds.map(async hostId => {
            const hostSnap = await getDoc(doc(db, 'users', hostId))
            if (hostSnap.exists()) {
              const data = hostSnap.data()
              hostMap.set(hostId, {
                displayName: data.displayName || 'Unknown',
                bankDetails: data.bankDetails || null
              })
            }
          })
        )

        await Promise.all(
          diveIds.map(async diveId => {
            const diveSnap = await getDoc(doc(db, 'dives', diveId))
            if (diveSnap.exists()) {
              const data = diveSnap.data()
              diveMap.set(diveId, data.siteName || 'Unknown Dive')
            }
          })
        )

        const resolved: PayoutRow[] = transactions.map(tx => ({
          transaction: tx,
          hostName: hostMap.get(tx.hostId)?.displayName || 'Unknown Host',
          bankDetails: hostMap.get(tx.hostId)?.bankDetails || null,
          diveName: diveMap.get(tx.diveId) || 'Unknown Dive'
        }))

        setPayouts(resolved)
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to load payouts.'
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    [showTestData]
  )

  useEffect(() => {
    const statuses = showCancelled
      ? [...activeFilters, 'cancelled' as PayoutFilter]
      : activeFilters.filter(f => f !== 'cancelled')
    // Deduplicate
    const unique = [...new Set(statuses)]
    if (unique.length > 0) {
      fetchPayouts(unique)
    } else {
      setPayouts([])
      setLoading(false)
    }
  }, [activeFilters, showCancelled, fetchPayouts])

  const toggleFilter = (filter: PayoutFilter) => {
    if (filter === 'cancelled') {
      setShowCancelled(!showCancelled)
      return
    }
    setActiveFilters(prev =>
      prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
    )
  }

  const handleMarkProcessed = (payout: PayoutRow) => {
    setSelectedPayout(payout)
    setNotes('')
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    if (!selectedPayout) return

    setProcessing(true)
    try {
      const functions = getFunctions()
      const markPayoutProcessed = httpsCallable<
        { transactionId: string; notes?: string },
        { success: boolean }
      >(functions, 'markPayoutProcessed')

      const payload: { transactionId: string; notes?: string } = {
        transactionId: selectedPayout.transaction.id
      }
      if (notes.trim()) {
        payload.notes = notes.trim()
      }

      await markPayoutProcessed(payload)

      setSnackbar({
        open: true,
        message: 'Payout marked as processed successfully.',
        severity: 'success'
      })
      setConfirmOpen(false)
      setSelectedPayout(null)
      setNotes('')
      // Refresh list
      const statuses = showCancelled
        ? [...activeFilters, 'cancelled' as PayoutFilter]
        : activeFilters.filter(f => f !== 'cancelled')
      fetchPayouts([...new Set(statuses)])
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string }
      if (error.code === 'functions/failed-precondition') {
        setSnackbar({
          open: true,
          message:
            'This transaction has already been modified. Refreshing list...',
          severity: 'error'
        })
        setConfirmOpen(false)
        const statuses = showCancelled
          ? [...activeFilters, 'cancelled' as PayoutFilter]
          : activeFilters.filter(f => f !== 'cancelled')
        fetchPayouts([...new Set(statuses)])
      } else {
        setSnackbar({
          open: true,
          message: error.message || 'Failed to process payout.',
          severity: 'error'
        })
      }
    } finally {
      setProcessing(false)
    }
  }

  const handleCancelConfirm = () => {
    setConfirmOpen(false)
    setSelectedPayout(null)
    setNotes('')
  }

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant='outlined'
          onClick={() => fetchPayouts([...new Set(currentStatuses)])}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Payout Management
      </Typography>

      {/* Status filters */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {STATUS_OPTIONS.filter(o => o.value !== 'cancelled').map(option => (
          <Chip
            key={option.value}
            label={option.label}
            variant={
              activeFilters.includes(option.value) ? 'filled' : 'outlined'
            }
            color={statusColor(option.value)}
            onClick={() => toggleFilter(option.value)}
            size='small'
          />
        ))}
        <FormControlLabel
          control={
            <Checkbox
              checked={showCancelled}
              onChange={() => setShowCancelled(!showCancelled)}
              size='small'
            />
          }
          label='Show cancelled'
          sx={{ ml: 1 }}
        />
      </Box>

      {loading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      ) : payouts.length === 0 ? (
        <Typography color='text.secondary'>
          No payouts match the selected filters.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size='small' aria-label='Payouts table'>
            <TableHead>
              <TableRow>
                <TableCell>Host</TableCell>
                <TableCell>Bank Details</TableCell>
                <TableCell>Net Owed</TableCell>
                <TableCell>Dive</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.map(payout => (
                <TableRow key={payout.transaction.id}>
                  <TableCell>{payout.hostName}</TableCell>
                  <TableCell>
                    {payout.bankDetails ? (
                      <Box>
                        <Typography variant='body2'>
                          {payout.bankDetails.accountName}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          BSB: {payout.bankDetails.bsb} | Acc:{' '}
                          {payout.bankDetails.accountNumber}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        No bank details (credit)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    ${payout.transaction.netOwedToHost?.toFixed(2) ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Link
                      component='button'
                      variant='body2'
                      onClick={() => handleDiveClick(payout)}
                      sx={{ cursor: 'pointer', textAlign: 'left' }}
                    >
                      {payout.diveName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payout.transaction.payoutStatus}
                      color={statusColor(payout.transaction.payoutStatus)}
                      size='small'
                      variant='outlined'
                    />
                  </TableCell>
                  <TableCell>
                    {payout.transaction.payoutStatus === 'pending' ? (
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => handleMarkProcessed(payout)}
                      >
                        Mark as Processed
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelConfirm}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Confirm Payout Processing</DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='body1' gutterBottom>
                <strong>Host:</strong> {selectedPayout.hostName}
              </Typography>
              <Typography variant='body1' gutterBottom>
                <strong>Amount:</strong> $
                {selectedPayout.transaction.netOwedToHost?.toFixed(2) ?? '—'}
              </Typography>
              {selectedPayout.bankDetails ? (
                <Box sx={{ mt: 1 }}>
                  <Typography variant='body2'>
                    <strong>Bank:</strong>{' '}
                    {selectedPayout.bankDetails.accountName}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>BSB:</strong> {selectedPayout.bankDetails.bsb}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>Account:</strong>{' '}
                    {selectedPayout.bankDetails.accountNumber}
                  </Typography>
                </Box>
              ) : (
                <Alert severity='info' sx={{ mt: 1 }}>
                  No bank details on file. This will be credited to the host's
                  account balance.
                </Alert>
              )}
            </Box>
          )}
          <TextField
            label='Notes (optional)'
            value={notes}
            onChange={e => {
              if (e.target.value.length <= 500) {
                setNotes(e.target.value)
              }
            }}
            multiline
            rows={3}
            fullWidth
            helperText={`${notes.length}/500 characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm} disabled={processing}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirm}
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dive Detail Modal */}
      <Dialog
        open={diveDetailOpen}
        onClose={() => {
          setDiveDetailOpen(false)
          setSelectedDiveDetail(null)
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Dive Details</DialogTitle>
        <DialogContent>
          {diveDetailLoading && (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          )}
          {!diveDetailLoading && !selectedDiveDetail && (
            <Typography color='text.secondary'>
              Dive details not available.
            </Typography>
          )}
          {!diveDetailLoading && selectedDiveDetail && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                pt: 1
              }}
            >
              <Typography variant='body2'>
                <strong>Title:</strong> {selectedDiveDetail.siteName}
              </Typography>
              <Typography variant='body2'>
                <strong>Host:</strong> {selectedDiveHostName}
              </Typography>
              <Typography variant='body2'>
                <strong>Description:</strong> {selectedDiveDetail.description}
              </Typography>
              <Typography variant='body2'>
                <strong>Location:</strong> {selectedDiveDetail.locationDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Date:</strong> {selectedDiveDetail.dateTimeDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Price:</strong>{' '}
                {selectedDiveDetail.price != null
                  ? formatAUD(selectedDiveDetail.price)
                  : 'Free'}
              </Typography>
              <Typography variant='body2'>
                <strong>Capacity:</strong> {selectedDiveDetail.maxParticipants}
              </Typography>
              <Typography variant='body2'>
                <strong>Type:</strong> {selectedDiveDetail.type}
              </Typography>
              <Typography variant='body2'>
                <strong>Duration:</strong> {selectedDiveDetail.duration} min
              </Typography>
              <Typography variant='body2'>
                <strong>Depth Tier:</strong> {selectedDiveDetail.depthTier}
              </Typography>
              <Typography variant='body2'>
                <strong>Status:</strong>{' '}
                <Chip
                  label={selectedDiveDetail.status}
                  color={
                    DIVE_STATUS_COLORS[selectedDiveDetail.status] ?? 'default'
                  }
                  size='small'
                />
              </Typography>
              {selectedDiveDetail.status === 'removed' &&
                selectedDiveDetail.moderationReason && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Alert severity='warning' sx={{ mt: 1 }}>
                      <strong>Removal reason:</strong>{' '}
                      {selectedDiveDetail.moderationReason}
                    </Alert>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDiveDetailOpen(false)
              setSelectedDiveDetail(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PayoutManagementTab
